import axios from "axios";
import { FormEvent, useContext, useEffect, useRef, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import styled from "styled-components";
import { ToastEnum, ToastStatusEnum, useToast } from "../../hooks/toast";
import { AuthContext } from "../../context/AuthContext";
import { ADMIN_API_URL } from "../../utils/env";
import { affiliateError } from "../../hooks/api/admin/useAffiliates";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { ApplicantAccount } from "./ApplicantAccount";
import { Collection } from "./Collection";
import { MakerApplication, MakerApplications } from "./Applications";
import { QuoteCommitmentEditor } from "./QuoteCommitmentEditor";
import {
  hoursSummary,
  nextTrial,
  quoteSummary,
  utcInput,
  utcValue,
  validateQuoteRows,
} from "./membershipForm";
import { MakerMonitoring } from "./Monitoring";
import { WeeklySettlement } from "./WeeklySettlement";
import { formatAEVO } from "./budget";
import { makerEnvironment, makerEnvironmentMatches } from "./environment";
import { LaunchPolicy, MakerLaunchPolicy } from "./LaunchPolicy";
import {
  QuoteCommitment,
  quoteWindow,
  optionCoverage,
} from "./quoteCommitments";

export interface Deal {
  id: string;
  market: string;
  start: string;
  end: string;
  rate_micro_bps: number;
  resume_rate_micro_bps: number;
}
export interface Terms {
  enabled: boolean;
  wallets: string[];
  markets: string[];
  scope_policy_ref: string;
  commitments: string;
  quoting_hours: string;
  quote_commitments?: QuoteCommitment[] | null;
  trial_start: string;
  trial_end: string;
  deals: Deal[];
}
interface Version {
  application_id?: string;
  number: number;
  effective_at: string;
  approved_at: string;
  approved_by: string;
  terms: Terms;
}
interface MakerCapabilities {
  enrollment_enabled: boolean;
  immediate_membership_activation?: boolean;
  approve_and_activate_membership?: boolean;
  execution_fees_enabled: boolean;
  collection_configured: boolean;
  claims_enabled: false;
}
export interface MakerState {
  capabilities?: MakerCapabilities;
  weekly_ledger?: {
    order: string[] | null;
    weeks: Record<
      string,
      {
        week_id: string;
        maker_paid_units: string;
        returned_to_trading_units: string;
        trading_total_units: string;
      }
    >;
  };
  schema_version: number;
  environment: string;
  testnet_only: boolean;
  server_time: string;
  launch_policy?: MakerLaunchPolicy;
  makers: { maker_id: string; versions: Version[] }[];
  receipts: { request_id: string; maker_id: string; version: number }[];
}
export interface MakerCommand {
  request_id: string;
  operation: "upsert_membership";
  payload: {
    maker_id: string;
    expected_version: number;
    effective_at?: string;
    effective_immediately?: boolean;
    application_id?: string;
    expected_application_revision?: number;
    terms: Terms;
  };
}
export function verifyMakerState(data: MakerState) {
  if (
    !makerEnvironmentMatches(data) ||
    data.schema_version !== 1 ||
    !Number.isFinite(Date.parse(data.server_time)) ||
    !Array.isArray(data.makers) ||
    !Array.isArray(data.receipts)
  )
    throw new Error(
      "Maker controls require a verified backend for this environment."
    );
  return data;
}
export function makerRequestID(serverTime: string) {
  const now = Date.parse(serverTime);
  if (!Number.isFinite(now))
    throw new Error("Refresh server time before submitting.");
  const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)), (n) =>
    n.toString(16).padStart(2, "0")
  ).join("");
  return `makerv1:${Math.floor(now / 1000) + 3600}:${nonce}`;
}
const Page = styled.main`
  height: calc(100vh - 110px);
  overflow: auto;
  box-sizing: border-box;
  min-height: 0;
  padding: 24px;
  #maker-membership-search {
    width: 320px;
    max-width: 100%;
    box-sizing: border-box;
  }
  .maker-section-heading > label {
    max-width: 100%;
  }
  color: ${TEXT_COLORS.one};
  .maker-page-heading,
  .maker-section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  .maker-environment {
    color: ${TEXT_COLORS.two};
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .maker-tabs {
    display: flex;
    gap: 24px;
    border-bottom: 1px solid ${BORDER_COLORS.one};
    margin: 20px 0 24px;
    overflow-x: auto;
  }
  .maker-tabs button {
    background: transparent;
    border: 0;
    border-bottom: 2px solid transparent;
    border-radius: 0;
    padding: 12px 0;
    color: ${TEXT_COLORS.two};
    white-space: nowrap;
  }
  .maker-tabs button[aria-selected="true"] {
    color: #40d4ef;
    border-bottom-color: #40d4ef;
  }
  .maker-primary {
    background: #40d4ef;
    color: #08090d;
    border-color: #40d4ef;
  }
  .maker-agreement-form {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
  }
  .maker-agreement-form label,
  .maker-agreement-form input,
  .maker-agreement-form textarea {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
  .maker-agreement-form input[type="datetime-local"] {
    color-scheme: dark;
  }
  .maker-agreement-form input[type="checkbox"] {
    width: auto;
    align-self: flex-start;
  }
  .maker-field-help {
    display: block;
    color: ${TEXT_COLORS.two};
    font-size: 12px;
    line-height: 1.5;
    font-weight: normal;
  }
  .maker-agreement-form h3 {
    margin: 12px 0 0;
    font-size: 16px;
  }
  .maker-agreement-form select {
    width: 100%;
    box-sizing: border-box;
  }
  .maker-quote-schedule {
    margin-top: 20px;
  }
  .maker-quote-actions {
    margin-top: 16px;
  }
  .maker-form-actions {
    flex-wrap: wrap;
    align-items: center;
  }
  .maker-form-note {
    padding: 12px 16px;
    border-left: 2px solid #40d4ef;
    background: ${BACKGROUND_COLORS.component};
  }
  .maker-agreement-form .maker-confirmation {
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }
  .maker-form-wide {
    grid-column: 1 / -1;
  }
  .maker-form-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    width: 100%;
  }
  .maker-quote-market {
    display: block;
    border-radius: 8px;
  }
  .maker-schedule-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
    gap: 12px;
    align-items: end;
    margin: 14px 0;
  }
  .maker-form-actions {
    display: flex;
    gap: 10px;
  }
  .maker-advanced {
    border-top: 1px solid ${BORDER_COLORS.one};
    margin-top: 20px;
    padding-top: 8px;
  }
  @media (max-width: 900px) {
    .maker-agreement-form,
    .maker-form-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .maker-schedule-row {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (max-width: 600px) {
    .maker-agreement-form,
    .maker-form-grid,
    .maker-schedule-row {
      grid-template-columns: minmax(0, 1fr);
    }
  }
  .maker-subtle {
    color: ${TEXT_COLORS.two};
    font-size: 13px;
  }
  .maker-member {
    border-bottom: 1px solid ${BORDER_COLORS.one};
    padding: 8px 0;
  }
  .maker-member summary {
    display: flex;
    align-items: center;
    gap: 20px;
    list-style: none;
    flex-wrap: wrap;
  }
  .maker-member summary strong {
    min-width: 180px;
  }
  .maker-member summary span {
    color: ${TEXT_COLORS.two};
    font-size: 13px;
  }
  .maker-member summary span:last-child {
    margin-left: auto;
  }
  h1 {
    font-size: 24px;
    margin: 0 0 8px;
  }
  h2 {
    font-size: 17px;
  }
  p {
    color: ${TEXT_COLORS.two};
    line-height: 1.5;
  }
  summary {
    cursor: pointer;
    padding: 10px 0;
  }
  dl {
    display: grid;
    grid-template-columns: minmax(120px, 180px) 1fr;
    gap: 10px;
  }
  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
  dt {
    color: ${TEXT_COLORS.two};
  }
  fieldset {
    border: 1px solid ${BORDER_COLORS.one};
    padding: 12px;
    margin: 12px 0;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  @media (max-width: 600px) {
    padding: 12px;
    section {
      padding: 12px;
    }
    dl {
      grid-template-columns: 1fr;
    }
  }
  section {
    background: ${BACKGROUND_COLORS.component};
    border: 1px solid ${BORDER_COLORS.one};
    border-radius: 8px;
    padding: 20px;
    margin: 16px 0;
  }
  form {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: end;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13px;
  }
  input,
  textarea,
  select,
  button {
    background: ${BACKGROUND_COLORS.eight};
    border: 1px solid ${BORDER_COLORS.two};
    color: inherit;
    border-radius: 4px;
    padding: 10px;
    font: inherit;
  }
  textarea {
    min-width: 260px;
  }
  button {
    cursor: pointer;
  }
  button:disabled {
    opacity: 0.45;
    cursor: default;
  }
  pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  td,
  th {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #30303b;
  }
`;
const emptyTerms: Terms = {
  enabled: true,
  wallets: [],
  markets: [],
  scope_policy_ref: "parent_family_v1",
  commitments: "",
  quoting_hours: "",
  trial_start: "",
  trial_end: "",
  deals: [],
};
export function Makers() {
  const { tokenId, userEmail } = useContext(AuthContext);
  return <MakersBody key={`${tokenId}:${userEmail}`} />;
}
function MakersBody() {
  const { addToast } = useToast();
  const notifyError = (message: string) => {
    addToast({
      type: ToastEnum.SIMPLE,
      status: ToastStatusEnum.ERROR,
      header: "Maker update failed",
      subheader: message,
    });
  };
  const { tokenId, userEmail, queryConfig } = useContext(AuthContext);
  const [applicationLink] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("application") || "";
    return {
      open: params.get("tab") === "applications",
      id: /^makerv1:\d+:[a-f0-9]{32}$/.test(id) ? id : "",
    };
  });
  const [activeTab, setActiveTab] = useState(
    applicationLink.open ? "Applications" : "Performance"
  );
  const [applicationCount, setApplicationCount] = useState(0);
  const [applicationProposal, setApplicationProposal] =
    useState<MakerApplication>();
  const { mutate: mutateCache } = useSWRConfig();
  const [proposedHours, setProposedHours] = useState("");
  const [existingMaker, setExistingMaker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSession, setEditSession] = useState(0);
  const [membershipSearch, setMembershipSearch] = useState("");
  const environment = makerEnvironment();
  const allowed = !!environment;
  const scope = `${process.env.REACT_APP_ENVIRONMENT}:${
    userEmail || tokenId || ""
  }`;
  const key = `aevo:maker-admin:pending:${scope}`;
  const [pendingState, setPending] = useState<{
    scope: string;
    command?: MakerCommand;
  }>();
  let stored: MakerCommand | undefined;
  try {
    stored = JSON.parse(sessionStorage.getItem(key) || "null") || undefined;
  } catch {
    /* memory retry remains */
  }
  const pending = pendingState?.scope === scope ? pendingState.command : stored;
  const remember = (command?: MakerCommand) => {
    setPending({ scope, command });
    try {
      if (command) sessionStorage.setItem(key, JSON.stringify(command));
      else sessionStorage.removeItem(key);
    } catch {
      /* memory retry remains */
    }
  };
  const state = useSWR<MakerState>(
    allowed && tokenId ? [ADMIN_API_URL("makers/state"), tokenId] : null,
    async () => {
      const response = await axios.get(
        ADMIN_API_URL("makers/state"),
        queryConfig()
      );
      if (response.status !== 200)
        throw new Error("Maker state requires a successful HTTP 200 response.");
      return verifyMakerState(response.data);
    },
    { refreshInterval: 5000, shouldRetryOnError: false }
  );
  const [makerID, setMakerID] = useState("");
  const [terms, setTerms] = useState<Terms>(emptyTerms);
  const [quoteCommitmentJSON, setQuoteCommitmentJSON] = useState("[]");
  const [effectiveAt, setEffectiveAt] = useState("");
  const [effectiveImmediately, setEffectiveImmediately] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const formRef = useRef<HTMLElement>(null);
  const formErrorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (editing) {
      setFormError("");
      formRef.current?.scrollIntoView?.({ block: "start" });
      formRef.current?.focus({ preventScroll: true });
    }
  }, [editing, editSession]);
  useEffect(() => {
    if (formError) {
      formErrorRef.current?.scrollIntoView?.({ block: "center" });
      formErrorRef.current?.focus({ preventScroll: true });
    }
  }, [formError]);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [review, setReview] = useState<MakerCommand>();
  const reviewRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (review) {
      reviewRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      });
      reviewRef.current?.focus({ preventScroll: true });
    }
  }, [review]);
  const [submitted, setSubmitted] = useState("");
  const history =
    state.data?.makers.find((m) => m.maker_id === makerID)?.versions || [];
  const applied = state.data?.receipts.find((r) => r.request_id === submitted);
  const receiptRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (submitted) {
      receiptRef.current?.scrollIntoView?.({ block: "center" });
      receiptRef.current?.focus({ preventScroll: true });
    }
  }, [submitted, applied?.maker_id, applied?.version]);
  const ready =
    allowed &&
    !!tokenId &&
    !!state.data &&
    !state.error &&
    state.data.capabilities?.enrollment_enabled === true;
  const capabilityText = (
    enabled: boolean | undefined,
    enabledLabel: string,
    disabledLabel: string
  ) => {
    if (!state.data || state.error) return "Unavailable";
    return enabled === true ? enabledLabel : disabledLabel;
  };
  let enrollmentUnavailable = "Enrollment is not enabled for this environment.";
  if (!tokenId)
    enrollmentUnavailable =
      "Sign in with your staff account to review this agreement.";
  else if (state.error)
    enrollmentUnavailable =
      "Server state could not refresh. Changes are disabled until it loads.";
  else if (!state.data)
    enrollmentUnavailable =
      "Loading server state before this agreement can be reviewed…";
  let rewardHistoryMessage = "";
  if (!state.data && !state.error)
    rewardHistoryMessage = "Loading recorded reward weeks…";
  else if (state.error)
    rewardHistoryMessage =
      "Reward history could not refresh. Refresh server state before relying on these totals.";
  else if (!state.data?.weekly_ledger?.order?.length)
    rewardHistoryMessage = "No recorded reward weeks.";
  const update = <K extends keyof Terms>(field: K, value: Terms[K]) => {
    setTerms((t) => ({ ...t, [field]: value }));
    setReview(undefined);
  };
  const prepare = (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setFormError("");
    try {
      if (!ready || !state.data || pending)
        throw new Error("Refresh state and resolve the pending command first.");
      if (
        !makerID.trim() ||
        !terms.wallets.some((w) => w.trim()) ||
        terms.wallets
          .filter((w) => w.trim())
          .some((w) => !/^0x[0-9a-fA-F]{40}$/.test(w))
      )
        throw new Error(
          "Enter a maker name and valid wallet addresses (0x followed by 40 characters)."
        );
      if (
        environment === "mainnet" &&
        terms.scope_policy_ref !== "parent_family_v1"
      )
        throw new Error("Mainnet membership requires parent_family_v1.");
      if (
        effectiveImmediately &&
        state.data.capabilities?.immediate_membership_activation !== true
      )
        throw new Error(
          "This backend does not support immediate activation yet."
        );
      if (
        ![
          ...(effectiveImmediately ? [] : [effectiveAt]),
          terms.trial_start,
          terms.trial_end,
        ].every((date) => Number.isFinite(Date.parse(date)))
      )
        throw new Error(
          "Choose an activation time, trial start and trial end in UTC."
        );
      const date = effectiveImmediately
        ? undefined
        : new Date(effectiveAt).toISOString();
      if (date && Date.parse(date) <= Date.parse(state.data.server_time))
        throw new Error(
          state.data.capabilities?.immediate_membership_activation === true
            ? "That start time has passed. Choose Activate now or select a later UTC time."
            : "That start time has passed. Select a later UTC time before reviewing."
        );
      let quoteCommitments = JSON.parse(quoteCommitmentJSON);
      if (!Array.isArray(quoteCommitments))
        throw new Error("Approved quote commitments must be a JSON array.");
      if (
        quoteCommitments.some(
          (commitment) =>
            !commitment ||
            typeof commitment.market !== "string" ||
            typeof commitment.minimum_side_notional_usd !== "string" ||
            !Array.isArray(commitment.utc_windows) ||
            commitment.utc_windows.some(
              (window: any) =>
                !window ||
                ![window.weekday, window.start_minute, window.end_minute].every(
                  Number.isInteger
                )
            )
        )
      )
        throw new Error(
          "Each quote commitment needs a market, a decimal minimum_side_notional_usd string and integer UTC window fields."
        );
      quoteCommitments = validateQuoteRows(quoteCommitments);
      if (
        terms.scope_policy_ref === "parent_family_v1" &&
        quoteCommitments.some(
          (commitment: QuoteCommitment) =>
            ["BTC-OPTIONS", "ETH-OPTIONS"].includes(commitment.market) &&
            (!Number.isInteger(commitment.minimum_option_strikes_per_expiry) ||
              (commitment.minimum_option_strikes_per_expiry ?? 0) < 1 ||
              (commitment.minimum_option_strikes_per_expiry ?? 0) > 5)
        )
      )
        throw new Error(
          "Each options commitment needs an approved minimum_option_strikes_per_expiry from 1 through 5; both calls and puts are required."
        );

      const trialStart = new Date(terms.trial_start).toISOString();
      const trialEnd = new Date(terms.trial_end).toISOString();
      if (Date.parse(trialEnd) <= Date.parse(trialStart))
        throw new Error("Trial end must be after trial start.");
      const wallets = applicationProposal
        ? [applicationProposal.account]
        : terms.wallets.map((w) => w.trim()).filter(Boolean);
      if (new Set(wallets.map((w) => w.toLowerCase())).size !== wallets.length)
        throw new Error("Each wallet should appear only once.");
      if (!existingMaker && history.length)
        throw new Error(
          "That maker name already exists. Open its saved membership to edit it, or choose a different name."
        );
      const normalized = {
        ...terms,
        wallets,
        markets: quoteCommitments.map((row: QuoteCommitment) => row.market),
        commitments: terms.commitments.trim() || quoteSummary(quoteCommitments),
        quoting_hours:
          terms.quoting_hours.trim() || hoursSummary(quoteCommitments),
        quote_commitments: quoteCommitments,
        trial_start: new Date(terms.trial_start).toISOString(),
        trial_end: new Date(terms.trial_end).toISOString(),
        deals: terms.deals.map((d) => ({
          ...d,
          start: new Date(d.start).toISOString(),
          end: new Date(d.end).toISOString(),
        })),
      };
      const command: MakerCommand = {
        request_id: makerRequestID(state.data.server_time),
        operation: "upsert_membership",
        payload: {
          maker_id: makerID.trim(),
          expected_version: history[history.length - 1]?.number || 0,
          ...(effectiveImmediately
            ? { effective_immediately: true }
            : { effective_at: date }),
          ...(applicationProposal
            ? {
                application_id: applicationProposal.id,
                expected_application_revision: applicationProposal.revision,
              }
            : {}),
          terms: normalized,
        },
      };
      if (applicationProposal) {
        if (state.data.capabilities?.approve_and_activate_membership !== true)
          throw new Error(
            "Application activation is unavailable. Refresh server state."
          );
        send(command);
      } else setReview(command);
    } catch (requestError) {
      const message = affiliateError(requestError);
      setFormError(message);
      notifyError(message);
    }
  };
  const send = async (command: MakerCommand) => {
    if (!ready || inFlight.current) return;
    if (
      command.payload.effective_immediately &&
      state.data?.capabilities?.immediate_membership_activation !== true
    ) {
      const message =
        "Immediate activation is unavailable. Refresh server state before submitting.";
      setError(message);
      notifyError(message);
      return;
    }
    if (
      command.payload.application_id &&
      state.data?.capabilities?.approve_and_activate_membership !== true
    ) {
      const message =
        "Application activation is unavailable. Refresh server state before submitting.";
      setError(message);
      notifyError(message);
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError("");
    remember(command);
    setSubmitted(command.request_id);
    try {
      const { data } = await axios.post(
        ADMIN_API_URL("makers/command"),
        command,
        { ...queryConfig(), timeout: 20000 }
      );
      if (
        !makerEnvironmentMatches(data) ||
        data.success !== true ||
        data.request_id !== command.request_id
      )
        throw new Error(
          "Unconfirmed command. Retry the identical request or inspect its receipt."
        );
      remember(undefined);
      setReview(undefined);
      await state.mutate();
      if (command.payload.application_id) {
        setEditing(false);
        setApplicationProposal(undefined);
        setActiveTab("Memberships");
        setMembershipSearch(command.payload.maker_id);
        await mutateCache([
          ADMIN_API_URL("makers/applications"),
          tokenId,
        ]).catch(() => undefined);
      }
    } catch (e) {
      const message = affiliateError(e);
      setError(message);
      // Show the failure immediately, even if receipt reconciliation is slow.
      notifyError(message);
      const response = (e as any)?.response;
      if (
        [400, 422].includes(response?.status) &&
        makerEnvironmentMatches(response.data || {}) &&
        response.data?.request_id === command.request_id &&
        typeof response.data?.error === "string"
      ) {
        // A rejected retry can follow an accepted request whose response was
        // lost. Reconcile before clearing; expiry alone is not non-acceptance.
        try {
          const latest = await state.mutate();
          const receipt = latest?.receipts.find(
            (r) => r.request_id === command.request_id
          );
          const versions = latest?.makers.find(
            (m) => m.maker_id === command.payload.maker_id
          )?.versions;
          if (receipt) {
            remember(undefined);
            setReview(undefined);
          } else if (
            latest &&
            // After expiry, a sequenced state at/after that boundary proves
            // no older in-flight request can later acquire this version.
            (!pending ||
              Date.parse(latest.server_time) >=
                Number(command.request_id.split(":")[1]) * 1000) &&
            (versions?.[versions.length - 1]?.number || 0) ===
              command.payload.expected_version
          ) {
            remember(undefined);
            setSubmitted("");
            setReview(undefined);
          }
        } catch {
          // Failed reconciliation leaves the exact command available to retry.
        }
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  let editorTitle = existingMaker ? "Edit maker agreement" : "Add maker";
  if (applicationProposal) editorTitle = "Review & activate maker";
  let activationHelp =
    "Schedule a UTC start time. Immediate activation is not available yet.";
  if (state.data?.capabilities?.immediate_membership_activation)
    activationHelp =
      "Choose Activate now to start immediately, or schedule a UTC start time.";
  if (effectiveImmediately)
    activationHelp =
      "Starts when the backend accepts your confirmation. Trial dates stay as shown below.";
  let submitLabel = "Review change";
  if (applicationProposal) {
    submitLabel = effectiveImmediately
      ? "Approve & activate"
      : "Approve & schedule";
    if (busy) submitLabel = "Activating…";
  }
  if (!allowed)
    return (
      <Page>
        <h1>Organic makers</h1>
        <p>Organic maker controls are unavailable for this environment.</p>
      </Page>
    );
  return (
    <Page>
      <div className="maker-page-heading">
        <div>
          <h1>Organic makers</h1>
          <span className="maker-environment">{environment}</span>
        </div>
        <button
          type="button"
          className="maker-primary"
          onClick={() => {
            setActiveTab("Memberships");
            setEditing(true);
            setEditSession((session) => session + 1);
            setMakerID("");
            setExistingMaker(false);
            setProposedHours("");
            setApplicationProposal(undefined);
            setEffectiveAt("");
            setEffectiveImmediately(false);
            setTerms({ ...emptyTerms, wallets: [], markets: [], deals: [] });
            setQuoteCommitmentJSON("[]");
            setReview(undefined);
          }}
        >
          Add maker
        </button>
      </div>
      <div
        className="maker-tabs"
        role="tablist"
        aria-label="Organic maker sections"
      >
        {[
          "Performance",
          "Applications",
          "Memberships",
          "Rewards",
          "Settings",
        ].map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            id={`maker-tab-${tab}`}
            aria-controls={`maker-panel-${tab}`}
            aria-selected={activeTab === tab}
            tabIndex={activeTab === tab ? 0 : -1}
            onKeyDown={(event) => {
              const tabs = [
                "Performance",
                "Applications",
                "Memberships",
                "Rewards",
                "Settings",
              ];
              let next = tabs.indexOf(tab);
              if (event.key === "ArrowRight") next = (next + 1) % tabs.length;
              else if (event.key === "ArrowLeft")
                next = (next + tabs.length - 1) % tabs.length;
              else if (event.key === "Home") next = 0;
              else if (event.key === "End") next = tabs.length - 1;
              else return;
              event.preventDefault();
              setActiveTab(tabs[next]);
              document.getElementById(`maker-tab-${tabs[next]}`)?.focus();
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
            {tab === "Applications" && applicationCount > 0
              ? ` (${applicationCount} pending)`
              : ""}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id="maker-panel-Performance"
        aria-labelledby="maker-tab-Performance"
        hidden={activeTab !== "Performance"}
      >
        <MakerMonitoring />
      </div>
      <div
        role="tabpanel"
        id="maker-panel-Applications"
        aria-labelledby="maker-tab-Applications"
        hidden={activeTab !== "Applications"}
      >
        <MakerApplications
          active={activeTab === "Applications"}
          initialApplicationID={applicationLink.id}
          ready={!!state.data && !state.error && !!tokenId}
          serverTime={state.data?.server_time}
          onboardingReady={
            ready &&
            state.data?.capabilities?.approve_and_activate_membership ===
              true &&
            state.data?.capabilities?.immediate_membership_activation === true
          }
          enrolledApplicationIDs={(state.data?.makers || []).flatMap((maker) =>
            maker.versions.flatMap((version) =>
              version.application_id ? [version.application_id] : []
            )
          )}
          onCount={setApplicationCount}
          onRefreshState={() => state.mutate()}
          onSetup={(application) => {
            const enrolled = state.data?.makers.find((maker) =>
              maker.versions.some(
                (version) => version.application_id === application.id
              )
            );
            if (enrolled) {
              setActiveTab("Memberships");
              setEditing(false);
              setReview(undefined);
              setMembershipSearch(enrolled.maker_id);
              return;
            }
            if (
              !ready ||
              !state.data ||
              state.data.capabilities?.approve_and_activate_membership !==
                true ||
              state.data.capabilities?.immediate_membership_activation !== true
            )
              return;
            const trialStart = new Date(state.data.server_time);
            setActiveTab("Memberships");
            setEditing(true);
            setEditSession((n) => n + 1);
            setMakerID("");
            setExistingMaker(false);
            setProposedHours(application.hours || "24/7");
            setEffectiveAt("");
            setEffectiveImmediately(true);
            setQuoteCommitmentJSON(
              JSON.stringify(
                application.markets.map((market) => ({
                  market,
                  minimum_side_notional_usd: "",
                  ...(market.endsWith("-OPTIONS")
                    ? { minimum_option_strikes_per_expiry: 2 }
                    : {}),
                  utc_windows:
                    !application.hours.trim() ||
                    /^24\/7(?: UTC)?$/i.test(application.hours.trim())
                      ? Array.from({ length: 7 }, (_, weekday) => ({
                          weekday,
                          start_minute: 0,
                          end_minute: 1440,
                        }))
                      : [],
                }))
              )
            );
            setReview(undefined);
            setApplicationProposal(application);
            setTerms({
              ...emptyTerms,
              wallets: [application.account],
              markets: [...application.markets],
              quoting_hours: "",
              trial_start: trialStart.toISOString(),
              trial_end: new Date(
                trialStart.getTime() + 14 * 86400000
              ).toISOString(),
              deals: [],
            });
          }}
        />
      </div>
      {!tokenId && <p>Sign in with your staff account.</p>}
      {(error || state.error) && (
        <p role="alert">
          {error || affiliateError(state.error)}{" "}
          {state.error && (
            <button
              type="button"
              disabled={state.isValidating}
              onClick={() => state.mutate()}
            >
              Retry server state
            </button>
          )}
        </p>
      )}
      {submitted && (
        <p role="status" ref={receiptRef} tabIndex={-1}>
          {applied
            ? `Applied: ${applied.maker_id}, version ${applied.version}.`
            : `Awaiting authoritative receipt: ${submitted}`}
        </p>
      )}
      {pending && (
        <section>
          <h2>Unconfirmed change</h2>
          <p>
            Retry this exact request to avoid creating duplicate versions.
            Refresh state to inspect its receipt.
          </p>
          <pre>{JSON.stringify(pending, null, 2)}</pre>
          <button
            type="button"
            disabled={!ready || busy}
            onClick={() => send(pending)}
          >
            Retry identical request
          </button>
          {state.data?.receipts.some(
            (r) => r.request_id === pending.request_id
          ) && (
            <button
              type="button"
              onClick={() => {
                setSubmitted(pending.request_id);
                remember(undefined);
              }}
            >
              Acknowledge applied receipt
            </button>
          )}
        </section>
      )}
      <div
        role="tabpanel"
        id="maker-panel-Settings"
        aria-labelledby="maker-tab-Settings"
        hidden={activeTab !== "Settings"}
      >
        <button type="button" onClick={() => state.mutate()}>
          Refresh
        </button>
        <button
          type="button"
          disabled={!state.data || !!state.error}
          onClick={() => {
            if (!state.data || state.error) return;
            const url = URL.createObjectURL(
              new Blob([JSON.stringify(state.data, null, 2)], {
                type: "application/json",
              })
            );
            const link = document.createElement("a");
            link.href = url;
            link.download = `organic-makers-${environment}-state.json`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }}
        >
          Download backend state
        </button>
        <section aria-label="Program configuration">
          <h2>Program configuration</h2>
          {!state.data && !state.error && <p>Loading program configuration…</p>}
          {state.error && (
            <p>Configuration could not refresh. Use Refresh to try again.</p>
          )}
          <p>
            Enrollment:{" "}
            {capabilityText(
              state.data?.capabilities?.enrollment_enabled,
              "Enabled for staff",
              "Not enabled"
            )}
          </p>
          <p>
            Maker fee execution:{" "}
            {capabilityText(
              state.data?.capabilities?.execution_fees_enabled,
              "Enabled",
              "Not enabled"
            )}
          </p>
          <p>
            Quote collection:{" "}
            {capabilityText(
              state.data?.capabilities?.collection_configured,
              "Configured",
              "Not configured"
            )}
          </p>
          <p>
            Configuration only. See Performance for collector health. Claims
            remain deferred.
          </p>
        </section>
        {environment === "testnet" && (
          <LaunchPolicy policy={state.data?.launch_policy} />
        )}
      </div>
      <div
        role="tabpanel"
        id="maker-panel-Memberships"
        aria-labelledby="maker-tab-Memberships"
        hidden={activeTab !== "Memberships"}
      >
        {editing && (
          <section
            ref={formRef}
            tabIndex={-1}
            aria-label="Maker agreement editor"
          >
            <div className="maker-section-heading">
              <h2>{editorTitle}</h2>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setReview(undefined);
                }}
              >
                Close form
              </button>
            </div>
            <p className="maker-subtle">
              {applicationProposal
                ? "Confirm the terms below. Approval and membership are saved together when you activate."
                : "Choose the agreed terms below, then review before activating. Nothing is saved until you confirm."}
            </p>
            {applicationProposal && (
              <div className="maker-form-note" role="note">
                <strong>{applicationProposal.contact}</strong>
                <p>
                  The applicant account is fixed. Agree the markets, size and
                  schedule with the maker.
                </p>
                <p>
                  Requested hours: {proposedHours}. Set the agreed schedule for
                  each market below.
                </p>
              </div>
            )}
            <form className="maker-agreement-form" onSubmit={prepare}>
              <h3 className="maker-form-wide">1. Who is the maker?</h3>
              <label htmlFor="maker-maker-id">
                Maker name
                <input
                  id="maker-maker-id"
                  aria-label="Maker name"
                  required
                  readOnly={existingMaker}
                  placeholder="e.g. Harbor Markets"
                  value={makerID}
                  aria-describedby="maker-name-help"
                  onChange={(e) => {
                    setMakerID(e.target.value);
                    setReview(undefined);
                  }}
                />
                <small id="maker-name-help" className="maker-field-help">
                  A unique name your team will recognize. This becomes the
                  permanent maker ID.
                </small>
              </label>
              {applicationProposal ? (
                <ApplicantAccount
                  account={applicationProposal.account}
                  proposedWallets={applicationProposal.wallets}
                />
              ) : (
                <label htmlFor="maker-wallets-comma-separated">
                  Trading wallets
                  <textarea
                    id="maker-wallets-comma-separated"
                    aria-label="Trading wallets"
                    required
                    rows={3}
                    placeholder="0x… · one wallet per line"
                    value={terms.wallets.join("\n")}
                    aria-describedby="maker-wallet-help"
                    onChange={(e) =>
                      update(
                        "wallets",
                        e.target.value.split(/[\n,]/).map((v) => v.trim())
                      )
                    }
                  />
                  <small id="maker-wallet-help" className="maker-field-help">
                    Paste one per line or separate with commas. Verify these
                    belong to the same maker; parent and sub-accounts are
                    treated as one participant.
                  </small>
                </label>
              )}
              <h3 className="maker-form-wide">2. When should it start?</h3>
              <label htmlFor="maker-activation-mode">
                Activation
                <select
                  id="maker-activation-mode"
                  aria-label="Activation"
                  value={effectiveImmediately ? "now" : "scheduled"}
                  onChange={(e) => {
                    const now = e.target.value === "now";
                    setEffectiveImmediately(now);
                    setReview(undefined);
                    setFormError("");
                    if (
                      now &&
                      !existingMaker &&
                      state.data &&
                      !terms.trial_start &&
                      !terms.trial_end
                    ) {
                      const start = new Date(state.data.server_time);
                      setTerms((t) => ({
                        ...t,
                        trial_start: start.toISOString(),
                        trial_end: new Date(
                          start.getTime() + 14 * 86400000
                        ).toISOString(),
                      }));
                    }
                  }}
                >
                  <option value="scheduled">Schedule a time</option>
                  <option
                    value="now"
                    disabled={
                      state.data?.capabilities
                        ?.immediate_membership_activation !== true
                    }
                  >
                    Activate now
                  </option>
                </select>
                <small className="maker-field-help">{activationHelp}</small>
              </label>
              <details
                className="maker-form-wide maker-advanced"
                open={!applicationProposal || !effectiveImmediately}
              >
                <summary>
                  {applicationProposal
                    ? `Trial · ${terms.trial_start.slice(
                        0,
                        10
                      )} to ${terms.trial_end.slice(0, 10)} UTC · Edit dates`
                    : "Trial dates and scheduling"}
                </summary>
                <div className="maker-form-wide maker-form-actions">
                  <button
                    type="button"
                    disabled={!state.data || existingMaker}
                    onClick={() => {
                      if (!state.data) return;
                      const trial = nextTrial(state.data.server_time);
                      setEffectiveAt(trial.start);
                      setEffectiveImmediately(false);
                      setTerms((t) => ({
                        ...t,
                        trial_start: trial.start,
                        trial_end: trial.end,
                      }));
                      setReview(undefined);
                    }}
                  >
                    Use next Monday · 2-week trial
                  </button>
                  <span className="maker-subtle">
                    All dates and times are UTC.
                  </span>
                </div>
                <label htmlFor="maker-effective-at-iso-date">
                  Activate terms at (UTC)
                  <input
                    id="maker-effective-at-iso-date"
                    aria-label="Activate terms at (UTC)"
                    type="datetime-local"
                    step="1"
                    required={!effectiveImmediately}
                    disabled={effectiveImmediately}
                    value={effectiveImmediately ? "" : utcInput(effectiveAt)}
                    onChange={(e) => {
                      setEffectiveAt(utcValue(e.target.value));
                      setReview(undefined);
                    }}
                  />
                  <small className="maker-field-help">
                    {effectiveImmediately
                      ? "The exact activation time is recorded when you submit."
                      : "When these fees and quoting terms take effect. Select a future UTC time."}
                  </small>
                </label>
                <label htmlFor="maker-enabled">
                  Membership status
                  <select
                    id="maker-enabled"
                    value={terms.enabled ? "enabled" : "disabled"}
                    onChange={(e) =>
                      update("enabled", e.target.value === "enabled")
                    }
                  >
                    <option value="enabled">
                      Active from the selected time
                    </option>
                    <option value="disabled">
                      Inactive from the selected time
                    </option>
                  </select>
                  <small className="maker-field-help">
                    Active membership can change this maker’s trading fees.
                  </small>
                </label>
                <label htmlFor="maker-trial-starts-iso-date">
                  Trial starts (UTC)
                  <input
                    id="maker-trial-starts-iso-date"
                    aria-label="Trial starts (UTC)"
                    type="datetime-local"
                    step="1"
                    required
                    value={utcInput(terms.trial_start)}
                    onChange={(e) =>
                      update("trial_start", utcValue(e.target.value))
                    }
                  />
                  <small className="maker-field-help">
                    Normally Monday at 00:00 UTC.
                  </small>
                </label>
                <label htmlFor="maker-trial-ends-iso-date">
                  Trial ends (UTC)
                  <input
                    id="maker-trial-ends-iso-date"
                    aria-label="Trial ends (UTC)"
                    type="datetime-local"
                    step="1"
                    required
                    value={utcInput(terms.trial_end)}
                    onChange={(e) =>
                      update("trial_end", utcValue(e.target.value))
                    }
                  />
                  <small className="maker-field-help">
                    Normally two weeks after the start. The end time is
                    exclusive.
                  </small>
                </label>
              </details>
              <QuoteCommitmentEditor
                key={`${makerID}:${editSession}`}
                onInvalidate={() => setReview(undefined)}
                value={quoteCommitmentJSON}
                onChange={(value) => {
                  setQuoteCommitmentJSON(value);
                  try {
                    const rows = JSON.parse(value);
                    if (
                      Array.isArray(rows) &&
                      rows.every((r) => r && typeof r.market === "string")
                    )
                      setTerms((t) => ({
                        ...t,
                        markets: rows.map((r) => r.market),
                      }));
                  } catch {
                    /* Keep malformed raw edits available for correction. */
                  }
                  setReview(undefined);
                }}
              />
              <details className="maker-form-wide maker-advanced">
                <summary>Optional settings · notes and rebates</summary>
                <label
                  className="maker-form-wide"
                  htmlFor="maker-quoting-commitments"
                >
                  Agreement notes (optional)
                  <textarea
                    id="maker-quoting-commitments"
                    aria-label="Agreement notes (optional)"
                    rows={2}
                    placeholder="e.g. BTC pilot agreed with the maker on 3 November"
                    value={terms.commitments}
                    onChange={(e) => update("commitments", e.target.value)}
                  />
                  <small className="maker-field-help">
                    Leave blank to use a summary of the market requirements.
                    These notes do not change scoring.
                  </small>
                </label>
                <details className="maker-form-wide maker-advanced">
                  <summary>Advanced · saved agreement details</summary>
                  <p className="maker-subtle">
                    Quoting hours below are descriptive only. The market
                    schedules above determine the checks.
                  </p>
                  <label htmlFor="maker-quoting-hours">
                    Saved hours description
                    <input
                      id="maker-quoting-hours"
                      value={terms.quoting_hours}
                      placeholder="Generated from the market schedules if left blank"
                      onChange={(e) => update("quoting_hours", e.target.value)}
                    />
                  </label>
                  <label htmlFor="maker-approved-scope-policy">
                    Account grouping policy
                    <input
                      id="maker-approved-scope-policy"
                      aria-label="Account grouping policy"
                      readOnly={environment === "mainnet"}
                      required
                      value={terms.scope_policy_ref}
                      onChange={(e) =>
                        update("scope_policy_ref", e.target.value)
                      }
                    />
                    <small className="maker-field-help">
                      Fixed for mainnet: the maker and its parent/sub-account
                      family count together.
                    </small>
                  </label>
                </details>
                <details className="maker-form-wide maker-advanced">
                  <summary>Temporary rebates (optional)</summary>
                  <p>
                    Rates use integer micro-bps: -500000 = -0.5 bps. End time is
                    exclusive; resume rate is explicit.
                  </p>
                  {terms.deals.map((d, i) => (
                    <fieldset key={i}>
                      <legend>Deal {i + 1}</legend>
                      {(
                        [
                          "id",
                          "market",
                          "start",
                          "end",
                          "rate_micro_bps",
                          "resume_rate_micro_bps",
                        ] as const
                      ).map((k) => (
                        <label key={k} htmlFor={`maker-deal-${i}-${k}`}>
                          {
                            {
                              id: "Deal ID",
                              market: "Market",
                              start: "Starts (ISO date)",
                              end: "Ends (ISO date)",
                              rate_micro_bps: "Rebate (micro-bps)",
                              resume_rate_micro_bps:
                                "Rate after expiry (micro-bps)",
                            }[k]
                          }
                          <input
                            id={`maker-deal-${i}-${k}`}
                            required
                            type={k.includes("bps") ? "number" : "text"}
                            step="1"
                            value={d[k]}
                            onChange={(e) =>
                              update(
                                "deals",
                                terms.deals.map((v, j) =>
                                  j === i
                                    ? {
                                        ...v,
                                        [k]: k.includes("bps")
                                          ? Number(e.target.value)
                                          : e.target.value,
                                      }
                                    : v
                                )
                              )
                            }
                          />
                        </label>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          update(
                            "deals",
                            terms.deals.filter((_, j) => j !== i)
                          )
                        }
                      >
                        Remove deal
                      </button>
                    </fieldset>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      update("deals", [
                        ...terms.deals,
                        {
                          id: "",
                          market: "",
                          start: "",
                          end: "",
                          rate_micro_bps: -500000,
                          resume_rate_micro_bps: 0,
                        },
                      ])
                    }
                  >
                    Add temporary rebate
                  </button>
                </details>
              </details>
              {applicationProposal && (
                <p className="maker-form-wide maker-subtle">
                  Zero maker fees apply on the agreed markets from activation.
                  Points start accruing from eligible quotes. Rebates require
                  their agreed terms.
                </p>
              )}
              {!ready && (
                <p className="maker-form-wide maker-subtle">
                  {enrollmentUnavailable}
                </p>
              )}
              {formError && (
                <p
                  ref={formErrorRef}
                  tabIndex={-1}
                  className="maker-form-wide"
                  role="alert"
                >
                  {formError}
                </p>
              )}
              <button
                className="maker-primary maker-form-wide"
                disabled={!ready || busy || !!pending}
                type="submit"
              >
                {submitLabel}
              </button>
            </form>
          </section>
        )}
        {review && (
          <section
            ref={reviewRef}
            tabIndex={-1}
            aria-label="Review maker agreement"
          >
            <h2>Review exact change</h2>
            <p>
              This appends a new effective version. Prior history is preserved.
            </p>
            <p>
              <strong>{review.payload.maker_id}</strong> ·{" "}
              {review.payload.effective_immediately
                ? "Effective immediately on confirmation"
                : `Effective ${review.payload.effective_at}`}
            </p>
            <TermsSummary terms={review.payload.terms} />
            <details>
              <summary>Exact membership payload</summary>
              <pre>{JSON.stringify(review.payload, null, 2)}</pre>
            </details>
            <button
              type="button"
              disabled={!ready || busy || !!pending}
              onClick={() => send(review)}
            >
              Submit reviewed change
            </button>
          </section>
        )}
        <section>
          <div className="maker-section-heading">
            <div>
              <h2>Maker memberships</h2>
              <p className="maker-subtle">
                Approved wallets, quoting terms and history.
              </p>
            </div>
            <label htmlFor="maker-membership-search">
              Find a maker
              <input
                id="maker-membership-search"
                type="search"
                placeholder="Maker name or wallet"
                value={membershipSearch}
                onChange={(event) => setMembershipSearch(event.target.value)}
              />
            </label>
          </div>
          {!state.data && !state.error && <p>Loading maker memberships…</p>}
          {state.error && (
            <p>
              Memberships could not refresh.{" "}
              {state.data
                ? "Showing the last loaded agreements; changes are disabled."
                : "Refresh server state to try again."}
            </p>
          )}
          {!state.error && state.data?.makers.length === 0 && (
            <p>No maker memberships.</p>
          )}
          {state.data &&
            state.data.makers.length > 0 &&
            !state.data.makers.some((m) =>
              [m.maker_id, ...m.versions.flatMap((v) => v.terms.wallets)].some(
                (value) =>
                  value
                    .toLowerCase()
                    .includes(membershipSearch.trim().toLowerCase())
              )
            ) && (
              <p>
                No makers match your search.{" "}
                <button type="button" onClick={() => setMembershipSearch("")}>
                  Clear search
                </button>
              </p>
            )}
          {state.data?.makers
            .filter((m) =>
              [m.maker_id, ...m.versions.flatMap((v) => v.terms.wallets)].some(
                (value) =>
                  value
                    .toLowerCase()
                    .includes(membershipSearch.trim().toLowerCase())
              )
            )
            .map((m) => (
              <details className="maker-member" key={m.maker_id}>
                <summary>
                  <strong>{m.maker_id}</strong>
                  <span>
                    Latest terms:{" "}
                    {m.versions[m.versions.length - 1]?.terms.enabled
                      ? "enabled"
                      : "disabled"}
                  </span>
                  <span>
                    {m.versions[m.versions.length - 1]?.terms.markets.join(
                      ", "
                    )}
                  </span>
                  <span>
                    {m.versions.length} saved{" "}
                    {m.versions.length === 1 ? "version" : "versions"} · View
                    terms
                  </span>
                </summary>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(true);
                    setEditSession((session) => session + 1);
                    setMakerID(m.maker_id);
                    setExistingMaker(true);
                    setProposedHours("");
                    setEffectiveAt("");
                    setEffectiveImmediately(false);
                    setApplicationProposal(undefined);
                    setQuoteCommitmentJSON(
                      JSON.stringify(
                        m.versions[m.versions.length - 1].terms
                          .quote_commitments || [],
                        null,
                        2
                      )
                    );
                    setTerms(
                      JSON.parse(
                        JSON.stringify({
                          ...m.versions[m.versions.length - 1].terms,
                          deals:
                            m.versions[m.versions.length - 1].terms.deals || [],
                        })
                      )
                    );
                    setReview(undefined);
                  }}
                >
                  Edit latest terms
                </button>
                {m.versions.map((v) => (
                  <div key={v.number}>
                    <h3>
                      Version {v.number} ·{" "}
                      {v.terms.enabled ? "Enabled" : "Disabled"}
                    </h3>
                    <p>
                      Effective {v.effective_at} · Approved by {v.approved_by}{" "}
                      at {v.approved_at}
                    </p>
                    <TermsSummary terms={v.terms} />
                    <details>
                      <summary>Exact saved terms</summary>
                      <pre>{JSON.stringify(v.terms, null, 2)}</pre>
                    </details>
                  </div>
                ))}
              </details>
            ))}
        </section>
      </div>
      <div
        role="tabpanel"
        id="maker-panel-Rewards"
        aria-labelledby="maker-tab-Rewards"
        hidden={activeTab !== "Rewards"}
      >
        {environment === "mainnet" && (
          <section aria-label="Recorded weekly accounting">
            <h2>Recorded weekly accounting</h2>
            {rewardHistoryMessage ? (
              <p>{rewardHistoryMessage}</p>
            ) : (
              (state.data?.weekly_ledger?.order || []).map((id) => {
                const week = state.data?.weekly_ledger?.weeks[id];
                return week ? (
                  <div key={id}>
                    <h3>{week.week_id}</h3>
                    <p>
                      Maker rewards: {formatAEVO(week.maker_paid_units)} AEVO
                    </p>
                    <p>
                      Returned to trading rewards:{" "}
                      {formatAEVO(week.returned_to_trading_units)} AEVO
                    </p>
                  </div>
                ) : null;
              })
            )}
            <p>This view does not publish rewards or move funds.</p>
          </section>
        )}
        {environment === "mainnet" && (
          <WeeklySettlement
            ready={ready}
            serverTime={state.data?.server_time}
            onRecorded={() => state.mutate()}
          />
        )}
        {environment === "testnet" && (
          <Collection ready={ready} launchPolicy={state.data?.launch_policy} />
        )}
      </div>
    </Page>
  );
}

function TermsSummary({ terms }: { terms: Terms }) {
  return (
    <>
      <dl>
        <dt>Status</dt>
        <dd>{terms.enabled ? "Enabled" : "Disabled"}</dd>
        <dt>Wallets</dt>
        <dd>{terms.wallets.join(", ")}</dd>
        <dt>Markets</dt>
        <dd>{terms.markets.join(", ")}</dd>
        <dt>Quoting commitments</dt>
        <dd>{terms.commitments}</dd>
        <dt>Quoting hours</dt>
        <dd>{terms.quoting_hours}</dd>
        <dt>Trial period</dt>
        <dd>
          {terms.trial_start} → {terms.trial_end}
        </dd>
        <dt>Scope policy</dt>
        <dd>{terms.scope_policy_ref}</dd>
      </dl>
      {!!terms.quote_commitments?.length && (
        <>
          <h3>Approved quote commitments</h3>
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Market</th>
                  <th>Minimum per side (USD)</th>
                  <th>Options coverage</th>
                  <th>Agreed UTC windows</th>
                </tr>
              </thead>
              <tbody>
                {terms.quote_commitments.map((commitment) => (
                  <tr key={commitment.market}>
                    <td>{commitment.market}</td>
                    <td>{commitment.minimum_side_notional_usd}</td>
                    <td>{optionCoverage(commitment)}</td>
                    <td>
                      {commitment.utc_windows.map(quoteWindow).join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {terms.deals?.length ? (
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Deal / market</th>
                <th>Maker rate</th>
                <th>Schedule</th>
                <th>After expiry</th>
              </tr>
            </thead>
            <tbody>
              {terms.deals.map((deal) => (
                <tr key={deal.id}>
                  <td>
                    {deal.id} · {deal.market}
                  </td>
                  <td>{deal.rate_micro_bps / 1000000} bps</td>
                  <td>
                    {deal.start} → {deal.end}
                  </td>
                  <td>{deal.resume_rate_micro_bps / 1000000} bps</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>Zero maker fee on covered markets. No temporary rebate deals.</p>
      )}
    </>
  );
}
